import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogIn, LogOut, LayoutDashboard, Newspaper } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const CREST_URL = "https://customer-assets.emergentagent.com/job_career-hub-538/artifacts/li6pjilk_LC-Crest-%28Transparent%29.png";

export default function Layout({ children, variant = "public" }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [hasBlog, setHasBlog] = useState(false);

  useEffect(() => {
    let cancel = false;
    // Blog nav link is only visible to the public when at least one post exists.
    api.get("/public/blog")
      .then(({ data }) => { if (!cancel) setHasBlog(Array.isArray(data) && data.length > 0); })
      .catch(() => { if (!cancel) setHasBlog(false); });
    return () => { cancel = true; };
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
      <header className="border-b-2 border-black bg-white sticky top-0 z-40" data-testid="app-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <img
              src={CREST_URL}
              alt="School crest"
              className="h-11 w-11 object-contain"
              data-testid="nav-crest"
            />
            <div className="leading-none">
              <div className="font-display font-black text-lg tracking-tight">Careers Hub</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Student Opportunities</div>
            </div>
          </Link>

          <nav className="flex items-center gap-2">
            {variant === "public" && (
              <Link to="/" className={`brut-chip ${location.pathname === "/" ? "brut-chip-active" : "brut-chip-inactive"}`} data-testid="nav-board">
                Board
              </Link>
            )}
            {variant === "public" && hasBlog && (
              <Link
                to="/blog"
                className={`brut-chip ${location.pathname.startsWith("/blog") ? "brut-chip-active" : "brut-chip-inactive"}`}
                data-testid="nav-blog"
              >
                <Newspaper className="h-3.5 w-3.5 mr-1" />
                Blog
              </Link>
            )}
            {user && user !== false ? (
              <>
                <Link to="/staff" className="brut-btn-secondary text-sm py-2 px-3 hidden sm:inline-flex" data-testid="nav-dashboard">
                  <LayoutDashboard className="h-4 w-4" />
                  Dashboard
                </Link>
                <button onClick={logout} className="brut-btn-secondary text-sm py-2 px-3" data-testid="nav-logout">
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Sign out</span>
                </button>
              </>
            ) : (
              <Link to="/staff/login" className="brut-btn-secondary text-sm py-2 px-3" data-testid="nav-login">
                <LogIn className="h-4 w-4" />
                <span className="hidden sm:inline">Staff Sign in</span>
              </Link>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t-2 border-black bg-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-sm text-zinc-600 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={CREST_URL} alt="" className="h-8 w-8 object-contain opacity-80" />
            <span className="font-medium italic">Ut Sint Unum</span>
          </div>
          <div className="font-medium">© {new Date().getFullYear()} Careers Hub — Share with your school community.</div>
        </div>
      </footer>
    </div>
  );
}
